import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import ChoiceList from "@/components/practice/ChoiceList";
import QuestionCard from "@/components/practice/QuestionCard";
import ExamCountdownTimer from "@/components/exam/ExamCountdownTimer";
import ExamQuestionNavigator from "@/components/exam/ExamQuestionNavigator";
import { track } from "@/lib/analytics";
import type { ExamQuestionForClient, SubmitExamPayload } from "@/lib/exam-api";
import type { UsStateCode } from "@/lib/states";

/**
 * apps/web :: components/exam :: ExamSession
 *
 * The active-session controller. Renders one question at a time via the
 * existing `QuestionCard` + `ChoiceList` (reused from practice-mode) plus
 * exam-specific chrome:
 *
 *   - `<ExamCountdownTimer>` pinned top-right (always visible).
 *   - `<ExamQuestionNavigator>` dots showing answered / unanswered state.
 *   - Prev / Next buttons + a Submit button that's always enabled.
 *
 * State:
 *
 *   - `currentIndex` — which question is visible. Drives the navigator's
 *     aria-current + the QuestionCard / ChoiceList render.
 *   - `answers` — Map<questionId, chosenChoiceId | null>. Seeded with
 *     `null` for every question so the submit payload always lists the
 *     full set of questionIds (server scoring requires that — nulls count
 *     as incorrect).
 *   - `confirmOpen` — truthy when the user clicked Submit and there are
 *     unanswered questions; we demand explicit confirmation before
 *     POSTing.
 *   - `submitting` — latched on submit to prevent double-fire.
 *
 * Timeout contract (Hard Rule #6 — "Real DMV doesn't wait"):
 *
 *   When `<ExamCountdownTimer>` fires `onTimeout` we submit IMMEDIATELY
 *   with whatever answers are in state. No modal, no confirm. The
 *   `autoSubmittingRef` guards against double-fires if the timer and a
 *   manual submit race.
 */
/**
 * ExamSession's submit callback. The second argument lets ExamPage tell
 * the analytics emission whether this submit was triggered by the user
 * clicking "Submit" (false) or the countdown expiring (true). Backend
 * payload is unchanged — `auto_submitted_on_timeout` is a client-side
 * funnel marker only, never round-tripped.
 */
export type ExamSubmitFn = (
  payload: SubmitExamPayload,
  meta: { autoSubmittedOnTimeout: boolean },
) => Promise<unknown>;

export interface ExamSessionProps {
  sessionId: string;
  questions: ExamQuestionForClient[];
  deadlineMs: number;
  onSubmit: ExamSubmitFn;
}

export default function ExamSession({
  sessionId,
  questions,
  deadlineMs,
  onSubmit,
}: ExamSessionProps) {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string | null>>(() => {
    const init: Record<string, string | null> = {};
    for (const q of questions) init[q.id] = null;
    return init;
  });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const confirmCancelRef = useRef<HTMLButtonElement>(null);
  const confirmOkRef = useRef<HTMLButtonElement>(null);
  // Analytics: emit exam.answer_submitted ONCE per question_index, not
  // per choice change. The set stores indices we've already counted.
  const emittedAnswerIndicesRef = useRef<Set<number>>(new Set());

  const current = questions[currentIndex];
  // Derive the session's state code from the first question — every
  // question in a session shares the same state per the backend contract.
  const sessionStateCode = questions[0]?.state_code as UsStateCode | undefined;

  const answeredByIndex = useMemo(() => {
    const map: Record<number, boolean> = {};
    questions.forEach((q, idx) => {
      map[idx] = answers[q.id] != null;
    });
    return map;
  }, [questions, answers]);

  const unansweredCount = useMemo(
    () => questions.filter((q) => answers[q.id] == null).length,
    [questions, answers],
  );

  const handleSelect = useCallback(
    (choiceId: string) => {
      if (!current) return;
      setAnswers((prev) => ({ ...prev, [current.id]: choiceId }));
      // Analytics: emit exam.answer_submitted ONCE per question_index.
      // Changing a choice on the same question must NOT double-fire; we
      // log "user answered question N" as an engagement marker, not every
      // click. `sessionStateCode` guards against the edge case of an
      // empty questions array (unreachable in practice but defensively
      // typed).
      if (
        sessionStateCode &&
        !emittedAnswerIndicesRef.current.has(currentIndex)
      ) {
        emittedAnswerIndicesRef.current.add(currentIndex);
        track("exam.answer_submitted", {
          state: sessionStateCode,
          question_index: currentIndex,
        });
      }
    },
    [current, currentIndex, sessionStateCode],
  );

  const handleJump = useCallback(
    (index: number) => {
      if (index < 0 || index >= questions.length) return;
      setCurrentIndex(index);
    },
    [questions.length],
  );

  const doSubmit = useCallback(async (auto: boolean) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const payload: SubmitExamPayload = {
        sessionId,
        answers: questions.map((q) => ({
          questionId: q.id,
          chosenChoiceId: answers[q.id] ?? null,
        })),
      };
      await onSubmit(payload, { autoSubmittedOnTimeout: auto });
    } finally {
      // Don't un-latch submitting — the parent switches to the results
      // view, and if submission failed we want the button to re-enable;
      // but for now the parent owns that transition.
      setSubmitting(false);
      submittingRef.current = false;
    }
  }, [answers, onSubmit, questions, sessionId]);

  const handleSubmitClick = useCallback(() => {
    if (submittingRef.current) return;
    if (unansweredCount > 0) {
      setConfirmOpen(true);
      return;
    }
    void doSubmit(false);
  }, [unansweredCount, doSubmit]);

  const handleConfirmOk = useCallback(() => {
    setConfirmOpen(false);
    void doSubmit(false);
  }, [doSubmit]);

  const handleConfirmCancel = useCallback(() => {
    setConfirmOpen(false);
  }, []);

  const handleTimeout = useCallback(() => {
    setConfirmOpen(false);
    void doSubmit(true);
  }, [doSubmit]);

  useEffect(() => {
    if (!confirmOpen) return;
    const timer = setTimeout(() => {
      confirmCancelRef.current?.focus();
    }, 0);
    return () => clearTimeout(timer);
  }, [confirmOpen]);

  function handleDialogKeyDown(ev: React.KeyboardEvent<HTMLDivElement>) {
    if (ev.key !== "Tab") return;
    const cancel = confirmCancelRef.current;
    const ok = confirmOkRef.current;
    if (!cancel || !ok) return;
    if (ev.shiftKey) {
      if (document.activeElement === cancel) {
        ev.preventDefault();
        ok.focus();
      }
    } else {
      if (document.activeElement === ok) {
        ev.preventDefault();
        cancel.focus();
      }
    }
  }

  if (!current) return null;

  return (
    <div data-testid="exam-session" className="flex flex-col gap-md">
      <header className="flex flex-wrap items-center justify-between gap-sm">
        <p className="text-sm font-semibold text-neutral-700">
          {t("exam.session.progressLabel", {
            current: currentIndex + 1,
            total: questions.length,
          })}
        </p>
        <ExamCountdownTimer
          deadlineMs={deadlineMs}
          onTimeout={handleTimeout}
        />
      </header>

      <ExamQuestionNavigator
        questionCount={questions.length}
        answered={answeredByIndex}
        currentIndex={currentIndex}
        onJump={handleJump}
      />

      <QuestionCard question={current} />
      <ChoiceList
        choices={current.choices}
        selectedChoiceId={answers[current.id] ?? null}
        onSelect={handleSelect}
        disabled={submitting}
      />

      <div className="flex flex-wrap items-center justify-between gap-sm">
        <div className="flex items-center gap-sm">
          <button
            type="button"
            data-testid="exam-prev"
            disabled={currentIndex === 0}
            onClick={() => handleJump(currentIndex - 1)}
            className="inline-flex items-center rounded-md border border-brand-primary/20 bg-white px-md py-sm text-sm font-semibold text-brand-primary transition-colors hover:bg-brand-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary disabled:cursor-not-allowed disabled:opacity-60"
          >
            {t("exam.session.prev")}
          </button>
          <button
            type="button"
            data-testid="exam-next"
            disabled={currentIndex === questions.length - 1}
            onClick={() => handleJump(currentIndex + 1)}
            className="inline-flex items-center rounded-md border border-brand-primary/20 bg-white px-md py-sm text-sm font-semibold text-brand-primary transition-colors hover:bg-brand-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary disabled:cursor-not-allowed disabled:opacity-60"
          >
            {t("exam.session.next")}
          </button>
        </div>
        <button
          type="button"
          data-testid="exam-submit"
          onClick={handleSubmitClick}
          disabled={submitting}
          className="inline-flex items-center rounded-md border border-brand-primary bg-brand-primary px-md py-sm text-sm font-semibold text-white transition-colors hover:bg-brand-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting
            ? t("exam.session.submitting")
            : t("exam.session.submit")}
        </button>
      </div>

      {confirmOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="exam-submit-confirm-title"
          data-testid="exam-submit-confirm"
          onKeyDown={handleDialogKeyDown}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-md"
        >
          <div className="max-w-md rounded-lg bg-white p-md shadow-lg sm:p-lg">
            <h2
              id="exam-submit-confirm-title"
              className="font-display text-lg font-bold text-brand-primary"
            >
              {t("exam.session.confirmTitle")}
            </h2>
            <p className="mt-sm text-sm text-neutral-800">
              {t("exam.session.confirmBody", { count: unansweredCount })}
            </p>
            <div className="mt-md flex justify-end gap-sm">
              <button
                ref={confirmCancelRef}
                type="button"
                data-testid="exam-submit-confirm-cancel"
                onClick={handleConfirmCancel}
                className="inline-flex items-center rounded-md border border-brand-primary/30 bg-white px-md py-sm text-sm font-semibold text-brand-primary transition-colors hover:bg-brand-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
              >
                {t("exam.session.confirmCancel")}
              </button>
              <button
                ref={confirmOkRef}
                type="button"
                data-testid="exam-submit-confirm-ok"
                onClick={handleConfirmOk}
                className="inline-flex items-center rounded-md border border-brand-primary bg-brand-primary px-md py-sm text-sm font-semibold text-white transition-colors hover:bg-brand-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
              >
                {t("exam.session.confirmOk")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
